/** Tipos do chat — alinhados a contracts/openapi/momyedu.yaml */

export const TIPO_MENSAGEM_CHAT = {
  TEXTO: 0,
  AUDIO: 1,
  IMAGEM: 2,
  ARQUIVO: 3,
  SISTEMA: 4,
} as const;

export type TipoMensagemChat = (typeof TIPO_MENSAGEM_CHAT)[keyof typeof TIPO_MENSAGEM_CHAT];

/** tipo_evento_chat — contracts/enums.yaml (so mensagens SISTEMA) */
export const TIPO_EVENTO_CHAT = {
  CONVERSA_FINALIZADA: 0,
  CONVERSA_REABERTA: 1,
  PARTICIPANTE_ADICIONADO: 2,
  PARTICIPANTE_REMOVIDO: 3,
  PARTICIPANTE_SAIU: 4,
  GRUPO_RENOMEADO: 5,
  GRUPO_FOTO_ALTERADA: 6,
  ADMIN_ALTERADO: 7,
} as const;

export type TipoEventoChat = (typeof TIPO_EVENTO_CHAT)[keyof typeof TIPO_EVENTO_CHAT];

/** tipo_reacao_chat — contracts/enums.yaml */
export const TIPO_REACAO_CHAT = {
  CURTIR: 0,
  AMAR: 1,
  RISOS: 2,
  SURPRESA: 3,
  TRISTE: 4,
  PALMAS: 5,
} as const;

export type TipoReacaoChat = (typeof TIPO_REACAO_CHAT)[keyof typeof TIPO_REACAO_CHAT];

export const REACAO_CHAT_OPCOES: ReadonlyArray<{
  codigo: TipoReacaoChat;
  nome: string;
  emoji: string;
}> = [
  { codigo: TIPO_REACAO_CHAT.CURTIR, nome: 'CURTIR', emoji: '👍' },
  { codigo: TIPO_REACAO_CHAT.AMAR, nome: 'AMAR', emoji: '❤️' },
  { codigo: TIPO_REACAO_CHAT.RISOS, nome: 'RISOS', emoji: '😂' },
  { codigo: TIPO_REACAO_CHAT.SURPRESA, nome: 'SURPRESA', emoji: '😮' },
  { codigo: TIPO_REACAO_CHAT.TRISTE, nome: 'TRISTE', emoji: '😢' },
  { codigo: TIPO_REACAO_CHAT.PALMAS, nome: 'PALMAS', emoji: '👏' },
];

export type TipoConversa = 'INDIVIDUAL' | 'GRUPO' | 'FAMILIA';

export type TipoParticipanteChat = 'COLABORADOR' | 'RESPONSAVEL';

export type PapelParticipanteChat = 'ADMIN' | 'MEMBRO';

export type ChatFoto = {
  id: number;
  urlThumb?: string | null;
  urlMedium?: string | null;
  expiraEmEpochSeconds?: number | null;
};

/** Participante polimorfico (OpenAPI ChatParticipante) — autor / outro. */
export type ChatParticipante = {
  tipo: TipoParticipanteChat;
  id: number;
  nome?: string | null;
  fotoUrl?: string | null;
  cargoNome?: string | null;
};

export type ConversaResumo = {
  id: number;
  tipo: TipoConversa;
  titulo?: string | null;
  foto?: ChatFoto | null;
  ultimaEm?: string | null;
  ultimaPreview?: string | null;
  ultimaTipo?: number | null;
  ultimaIdAutor?: number | null;
  naoLidas: number;
  minhaUltima: boolean;
  /** 1:1 colaborador — id do outro; null em FAMILIA. */
  idOutroColaborador?: number | null;
  /** Outro lado (FAMILIA = responsavel; 1:1 = colaborador). */
  outro?: ChatParticipante | null;
};

export type ParticipanteChat = {
  /** Ausente quando o participante e familiar. */
  idColaborador?: number | null;
  nome: string;
  papel: PapelParticipanteChat;
  foto?: ChatFoto | null;
  /** Cargo do colaborador, ou parentesco do familiar. */
  cargoNome?: string | null;
  autor?: ChatParticipante | null;
};

export type ConversaDetalhe = ConversaResumo & {
  participantes: ParticipanteChat[];
};

export type ChatResumo = {
  totalNaoLidas: number;
};

export type MensagemAnexo = {
  id: number;
  idArquivo: number;
  ordem?: number;
  nome?: string | null;
  tipoConteudo?: string | null;
  urlThumb?: string | null;
  urlMedium?: string | null;
};

export type ReacaoAgregada = {
  codigo: number;
  total: number;
  minha: boolean;
};

export type LeituraParticipante = {
  idColaborador: number;
  nome: string;
};

export type LeiturasMensagem = {
  lidos: LeituraParticipante[];
  pendentes: LeituraParticipante[];
};

export type Mensagem = {
  id: number;
  idConversa: number;
  /** Id do colaborador autor; 0 quando o autor e responsavel. */
  idColaboradorAutor: number;
  nomeAutor: string;
  /** Autor polimorfico (colaborador ou responsavel). */
  autor?: ChatParticipante | null;
  tipo: number;
  /** tipo_evento_chat — presente quando tipo=SISTEMA */
  codigoEvento?: number | null;
  texto?: string | null;
  idMensagemResposta?: number | null;
  respostaPreview?: string | null;
  anexos?: MensagemAnexo[];
  clienteUuid: string;
  encaminhada: boolean;
  editadaEm?: string | null;
  criadoEm: string;
  lidaPeloOutro?: boolean | null;
  duracaoSegundos?: number | null;
  reacoes?: ReacaoAgregada[];
};

/** Titulo exibido na lista / header (escola: nome do responsavel em FAMILIA). */
export function tituloConversa(c: Pick<ConversaResumo, 'titulo' | 'outro'>): string {
  return c.titulo?.trim() || c.outro?.nome?.trim() || 'Conversa';
}

/** Nome do autor na bolha — prioriza autor.nome quando RESPONSAVEL. */
export function nomeAutorMensagem(m: Pick<Mensagem, 'nomeAutor' | 'autor'>): string {
  if (m.autor?.tipo === 'RESPONSAVEL') {
    const nome = m.autor.nome?.trim();
    if (nome) return nome;
  }
  return m.nomeAutor;
}

export type MensagemRequest = {
  clienteUuid: string;
  tipo: number;
  texto?: string | null;
  idMensagemResposta?: number | null;
  idsArquivos?: number[];
  duracaoSegundos?: number | null;
};

export type ConversaIndividualRequest = {
  idColaborador: number;
};

export type ConversaFamiliaRequest = {
  idResponsavel: number;
};

export type ConversaGrupoRequest = {
  titulo: string;
  idsColaboradores?: number[];
  idsResponsaveis?: number[];
  idsAlunos?: number[];
  idArquivoFoto?: number | null;
};

export type ChatElegivelColaborador = {
  idColaborador: number;
  nome: string;
  cargoNome?: string | null;
  fotoUrl?: string | null;
};

export type ChatElegivelFamiliar = {
  idResponsavel: number;
  nome: string;
  fotoUrl?: string | null;
  parentesco?: string | null;
  idAluno: number;
  nomeAluno: string;
  sexoAluno?: number | null;
  fotoAlunoUrl?: string | null;
};

export type EncaminharDestino = {
  idConversa: number;
  clienteUuid: string;
};

export type ChatEventoTipo =
  | 'MENSAGEM_NOVA'
  | 'CONVERSA_ATUALIZADA'
  | 'LEITURA'
  | 'MENSAGEM_OCULTA'
  | 'DIGITANDO'
  | 'PARTICIPANTES'
  | 'MENSAGEM_EDITADA'
  | 'REACAO'
  | 'PRESENCA';

export type ChatEvento = {
  tipo: ChatEventoTipo;
  idConversa?: number | null;
  mensagem?: Mensagem | null;
  conversa?: ConversaResumo | null;
  idMensagem?: number | null;
  idColaborador?: number | null;
  nome?: string | null;
  ativo?: boolean | null;
  online?: boolean | null;
  totalNaoLidas?: number | null;
  reacoes?: ReacaoAgregada[] | null;
};
